import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, uploadFile } from "../../../Utils/FileUpload";
import {
  Submit,
  AddVehicleModelTitle,
  VehicleModelTitle,
} from "../../../Utils/Constants";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import "react-toastify/dist/ReactToastify.css";
import useCustomToasts from "../../../hooks/useCustomToast";

interface FormValues {
  model_name: string;
  brand_id: string;
  image: FileList;
  transmission: string;
  year: string;
}
interface ErrorResponse {
  message: string;
}

export default function AddVehicleModels() {
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>();
  const [brands, setBrands] = useState<{ id: number; brand_name: string }[]>(
    []
  );

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${baseUrl}brand/getall`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        // console.log(response.data)
        const filteredBrands = response.data.filter(
          (brand: any) => brand.is_deleted === 0 && brand.is_active === 1
        );

        setBrands(filteredBrands);
      } catch (error) {
        console.error("Error fetching brands:", error);
      }
    };

    fetchBrands();
  }, []);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      const file = data.image?.[0];

      if (!file) {
        toast.error("Please upload a image.");
        return;
      }

      const fileName = await uploadFile(file);

      const payload = {
        ...data,
        image: fileName,
      };

      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${baseUrl}vehicle_model/store`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        SuccessToast("Data added successfully", "/view_model");
      } else {
        ErrorToast("Failed to add");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorMessage =
        (axiosError.response?.data as ErrorResponse)?.message ||
        "An unexpected error occurred";

      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={AddVehicleModelTitle}
        parent={VehicleModelTitle}
        title={AddVehicleModelTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={12}>
                  <Label htmlFor="model-name">Model Name</Label>
                  <Controller
                    name="model_name"
                    control={control}
                    rules={{
                      required: "Model name is required",
                      pattern: {
                        value: /^[A-Za-z0-9\s.'-]{2,50}$/,
                        message:
                          "Invalid model name. Only letters,dots, numbers, spaces, hyphens (-), and apostrophes (') are allowed.",
                      },
                      validate: (value) => {
                        // Ensure it's not all numbers or just special characters

                        if (/^\d+$/.test(value)) {
                          return "Model name cannot be all numbers.";
                        }
                        if (/^[^A-Za-z]+$/.test(value)) {
                          return "Model name must include letters.";
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <Input
                          id="model-name"
                          type="text"
                          placeholder="Model name"
                          {...field}
                          invalid={!!errors.model_name}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.model_name ? "red" : "",
                          }}
                          autoComplete="off"
                          onKeyDown={handleKeyDown}
                        />
                        {errors.model_name && (
                          <FormFeedback>
                            {errors.model_name.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="brand_id">Brand</Label>
                  <Controller
                    name="brand_id"
                    control={control}
                    rules={{ required: "Please select a brand" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="select"
                          {...field}
                          id="brand_id"
                          invalid={!!errors.brand_id}
                        >
                          <option value="" style={{ color: "adb5bd" }}>
                            -- Select Brand --
                          </option>
                          {brands.map((brand) => (
                            <option key={brand.id} value={brand.id}>
                              {brand.brand_name}
                            </option>
                          ))}
                        </Input>
                        {errors.brand_id && (
                          <FormFeedback>{errors.brand_id.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label className="w-100">Transmission</Label>
                  <Controller
                    name="transmission"
                    control={control}
                    render={({ field }) => {
                      const handleChange = (value: string) => {
                        field.onChange(field.value === value ? "" : value);
                      };

                      return (
                        <>
                          <div className="d-flex gap-4 text-body">
                            {["Manual", "Automatic", "Both"].map((option) => (
                              <div key={option} className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={field.value === option}
                                  onChange={() => handleChange(option)}
                                  id={`transmission-${option}`}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`transmission-${option}`}
                                >
                                  {option}
                                </label>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    }}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="year" className="w-100">
                    Year
                  </Label>
                  <Controller
                    name="year"
                    control={control}
                    rules={{ required: "Year is required" }}
                    render={({ field }) => (
                      <>
                        <DatePicker
                          selected={
                            field.value
                              ? new Date(Number(field.value), 0)
                              : null
                          }
                          onChange={(date: Date) => {
                            field.onChange(date.getFullYear().toString());
                          }}
                          showYearPicker
                          dateFormat="yyyy"
                          className={`form-control ${
                            errors.year ? "is-invalid" : ""
                          }`}
                          placeholderText="Select year"
                        />
                        {errors.year && (
                          <div className="invalid-feedback d-block">
                            {errors.year.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="formFile">Image</Label>
                  <Controller
                    name="image"
                    control={control}
                    rules={{ required: "Image is required" }}
                    render={({ field: { onChange, onBlur, ref } }) => (
                      <>
                        <Input
                          id="formImage"
                          type="file"
                          accept="image/*"
                          onChange={(e) => onChange(e.target.files)}
                          onBlur={onBlur}
                          innerRef={ref}
                          invalid={!!errors.image}
                          style={{ borderColor: errors.image ? "red" : "" }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.image && (
                          <FormFeedback>{errors.image.message}</FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {Submit}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
